
import { useState, useEffect } from 'react';
import MiniAppLayout from './MiniAppLayout';
import MiniAppDashboard from './MiniAppDashboard';
import MiniAppEngagement from './MiniAppEngagement';
import MiniAppWagers from './MiniAppWagers';
import MiniAppRoulette from './MiniAppRoulette';
import MiniAppProfile from './MiniAppProfile';
import JobsView from './Jobs/JobsView';

import MiniAppInitiation from './MiniAppInitiation';
import MiniAppLoader from './components/MiniAppLoader';
import MiniAppError from './components/MiniAppError';

import { useMiniAppUser } from './hooks/useMiniAppUser';
import { useTelegramWebAppSetup } from './hooks/useTelegramWebAppSetup';
import { isMiniAppView, getValidMiniAppView } from './utils/typeGuards';
import type { MiniAppView } from './types';

const MiniAppPage = () => {
  const [currentView, setCurrentView] = useState<MiniAppView>('dashboard');
  const [isInitialized, setIsInitialized] = useState(false);
  const { isReady } = useTelegramWebAppSetup();
  const { 
    user, 
    isAuthenticated, 
    isLoading, 
    error, 
    isTelegram, 
    retryCount, 
    handleRetryWithBackoff 
  } = useMiniAppUser();

  useEffect(() => {
    const initializeRouting = () => {
      console.log('Initializing mini-app routing...');
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const view = urlParams.get('view');
        
        const hash = window.location.hash.replace('#', '');
        const hashParts = hash.split('/');
        const hashView = hashParts[0];
        
        const targetView = view || hashView || 'dashboard';
        const finalView = getValidMiniAppView(targetView);
        
        console.log('Setting initial view to:', finalView);
        setCurrentView(finalView);
        
        console.log('Routing initialization complete:', {
          urlView: view,
          hashView: hashView,
          finalView: finalView,
          isValid: isMiniAppView(targetView)
        });
        
      } catch (error) {
        console.error('Error parsing URL parameters:', error);
        setCurrentView('dashboard');
      }
      
      setIsInitialized(true);
    };

    if (isReady) {
      setTimeout(initializeRouting, 100);
    }
  }, [isReady]);

  useEffect(() => {
    const handleTelegramNavigation = (event: PopStateEvent) => {
      console.log('Navigation event detected:', event.state);
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const view = urlParams.get('view');
        const finalView = getValidMiniAppView(view);
        
        console.log(`Navigating to view: ${finalView}`);
        setCurrentView(finalView);
      } catch (error) {
        console.error('Error handling navigation:', error);
        setCurrentView('dashboard');
      }
    };

    window.addEventListener('popstate', handleTelegramNavigation);
    return () => window.removeEventListener('popstate', handleTelegramNavigation);
  }, []);

  const handleNavigate = (view: MiniAppView) => {
    console.log(`Navigating from ${currentView} to ${view}`);
    setCurrentView(view);
    
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const newUrl = `${baseUrl}?view=${view}`;
      
      window.history.replaceState({ view }, '', newUrl);
      console.log(`URL updated to: ${newUrl}`);
    } catch (error) {
      console.error('Error updating URL:', error);
    }
  };

  if (!isInitialized || !isReady || isLoading) {
    console.log('Showing loader:', { isInitialized, isReady, isLoading });
    return <MiniAppLoader />;
  }

  // Handle initiation flow first - no authentication required
  if (currentView === 'initiation') {
    console.log('Showing initiation flow (no authentication required)');
    return <MiniAppInitiation onComplete={() => handleNavigate('dashboard')} />;
  }

  // Handle non-Telegram environment
  if (!isTelegram) {
    console.log('Showing error for non-Telegram environment');
    return (
      <MiniAppError 
        error="Mini-app must be accessed through Telegram" 
        retryCount={0} 
        onRetry={() => {}} 
      />
    );
  }

  // Handle errors for authenticated views
  if (error) {
    console.log('Showing error state for authenticated views:', error);
    return (
      <MiniAppError 
        error={error} 
        retryCount={retryCount} 
        onRetry={handleRetryWithBackoff} 
      />
    );
  }

  // Handle unauthenticated users - redirect to initiation
  if (!isAuthenticated) {
    console.log('Redirecting unauthenticated user to initiation');
    setCurrentView('initiation');
    return <MiniAppInitiation onComplete={() => handleNavigate('dashboard')} />;
  }

  const renderCurrentView = () => {
    if (!user) {
      console.log('No user data for authenticated view, showing loader');
      return <MiniAppLoader />;
    }
    
    console.log(`Rendering view: ${currentView} for user: ${user?.brocode || 'unauthenticated'}`);
    
    // Use explicit type assertion to avoid type narrowing
    const view = currentView as MiniAppView;
    
    switch (view) {
      case 'engagement':
        return <MiniAppEngagement user={user!} />;
      case 'jobs':
        return <JobsView user={user!} onNavigate={handleNavigate} />;
      case 'wagers':
        return <MiniAppWagers user={user!} />;
      case 'roulette':
        return <MiniAppRoulette user={user!} />;
      case 'profile':
        return <MiniAppProfile user={user!} />;
      case 'initiation':
        return <MiniAppInitiation onComplete={() => handleNavigate('dashboard')} />;
      case 'dashboard':
      default:
        return <MiniAppDashboard user={user!} onNavigate={handleNavigate} />;
    }
  };

  return (
    <MiniAppLayout currentView={currentView} onNavigate={handleNavigate} user={user}>
      {renderCurrentView()}
    </MiniAppLayout>
  );
};

export default MiniAppPage;
